/**
 * Checklist field (`field--checklist.twig`): a capped checkbox list with an
 * optional filter. It keeps the checked IDs in pick order and dispatches a
 * bubbling `checklist-change` event with `{name, values}` on every change, so a
 * parent component can react without the checklist knowing about it.
 */
import type { AlpineComponent } from '~/js/offer-generator/alpine'

export interface ChecklistConfig {
    /** Identifies the list in the change event */
    name: string
    /** Checked IDs, in pick order */
    selected: number[]
    /** Most checked boxes; null for no cap */
    max: number | null
}

export interface ChecklistChangeDetail {
    name: string
    values: number[]
}

interface ChecklistData {
    filter: string
    selected: number[]
    readonly full: boolean
    readonly hasMatches: boolean
    isChecked(id: number): boolean
    isDisabled(id: number): boolean
    matches(title: string): boolean
    toggle(id: number, checked: boolean): void
}

export default (config: ChecklistConfig): AlpineComponent<ChecklistData> => ({
    filter: '',
    selected: [...config.selected],

    get full(): boolean {
        return config.max !== null && this.selected.length >= config.max
    },

    get hasMatches(): boolean {
        const items: HTMLElement[] = Array.from(this.$refs.list?.querySelectorAll<HTMLElement>('[data-title]') ?? [])

        return items.some(item => this.matches(item.dataset.title ?? ''))
    },

    isChecked(id: number): boolean {
        return this.selected.includes(id)
    },

    isDisabled(id: number): boolean {
        return this.full && !this.isChecked(id)
    },

    matches(title: string): boolean {
        const query = this.filter.trim().toLowerCase()

        return query === '' || title.includes(query)
    },

    toggle(id: number, checked: boolean): void {
        if (checked && !this.isChecked(id) && !this.full) {
            this.selected = [...this.selected, id]
        } else if (!checked) {
            this.selected = this.selected.filter(selectedId => selectedId !== id)
        }

        const detail: ChecklistChangeDetail = { name: config.name, values: [...this.selected] }
        this.$dispatch('checklist-change', detail)
    },
})
